import { FieldError } from '@HoudiniUI/Field';
import {
	getRegEdRevisesApprovalRequestId,
	IRegEdSubmissionMetadata,
	IRegEdSubmissionView,
	isRegEdResendableFailure,
	useRegEdMetadataSchemaQuery,
} from '@Queries/RegEdCompliance';
import { THEME } from '@Theme';
import * as Api from '@ViewModels';
import { UseMutationResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { dedupeByProvider } from '../../../../extViewmodels/Utils';
import { SocialMediaPostStatusDetails } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import {
	accountAllowsComplianceBypass,
	applyFacebookMentions,
	applyPlatformMentions,
	carrySendForward,
	getStatusGroupOptions,
} from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { wasApprovedForCompliantSocialSend } from '../../../../models/utils/contentCalendarBulkAiUtils';
import { findPlatformCustomization, hasPublishedContent } from '../../../../models/utils/platformCustomizations';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import { Dialog, DialogDescription, DialogHeader, DialogPopup, DialogTitle } from '../../../components-v2/Dialog';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { Input } from '../../../components-v2/Input';
import { PopoverType, TinyPopover } from '../../../components/TinyPopover';
import { Toggle } from '../../../components/Toggle';
import { SimpleTooltip, Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { CampaignDetailsSummary } from '../../../components/campaigns/CampaignDetailsSummary';
import { ScheduleDate } from '../../../components/campaigns/CampaignDetailsSummary/presentation';
import { RegEdComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { Collapsible as CollapsibleV2, CollapsiblePanel, CollapsibleTrigger } from '../../../components-v2/Collapsible';
import { CampaignDateTimePicker } from '../../../components/campaigns/CampaignDateTimePicker';
import { RegEdComplianceStatus } from '../../../components/compliance/RegEdComplianceStatus';
import { findReviewMinimumDate } from '../../../components/compliance/RegEdMetadataForm/regEdMetadataFormAdapter';
import { RegEdReviseDateNotice, getRegEdReviseDateState } from '../../../components/compliance/RegEdReviseDateNotice';
import { RegEdRevisionOwedCard } from '../../../components/compliance/RegEdRevisionOwedCard';
import { buildRegEdSocialReviseLocation } from '../../../components/compliance/regEdReviseSocial';
import { IPlatformCustomizationView } from '../../../components/socialMedia/PlatformCustomization/usePlatformCustomization';
import { PreviewSocialMediaPost } from '../../../components/socialMedia/PreviewSocialMediaPost';
import { ChevronDown } from '../../../components/svgs/icons/ChevronDown';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { ExpiryIcon } from '../../../components/svgs/icons/ExpiryIcon';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { brandPrimary, charmGray, warningDark } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { IEditSocialMediaPostContext } from './context';
import { styleSheet } from './styles';

interface SocialMediaNavigationProps {
	onAIContentGenerationAssistantButtonClicked: () => void;
	canSaveTemplate: boolean;
	updateTemplateMutation: UseMutationResult<
		unknown,
		Api.IOperationResultNoValue,
		{
			template: Api.ITemplate;
		},
		unknown
	>;
	handleSave: () => void;
	onSaveAsTemplateClicked: () => void;
	newPost: boolean;
	impersonationCtx: Api.IImpersonationContext;
	context: IEditSocialMediaPostContext;
	onSaveAsButtonClicked: () => void;
	source: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	onShowCancelConfirmation: () => void;
	onShowUpdateConfirmation: () => void;
	/** True while a platform version breaks a limit the API enforces before publishing. */
	hasPlatformValidationErrors?: boolean;
	isBrandingInProgress?: boolean;
	/** True while one breaks a limit the API enforces on every save, draft included. */
	hasPlatformSaveErrors?: boolean;
	/** True while the post is split into a version per platform. */
	isSplitByPlatform?: boolean;
}

export const SocialMediaNavigation = ({
	onAIContentGenerationAssistantButtonClicked,
	canSaveTemplate,
	updateTemplateMutation,
	handleSave,
	onSaveAsTemplateClicked,
	newPost,
	impersonationCtx,
	context,
	onSaveAsButtonClicked,
	source,
	onShowCancelConfirmation,
	onShowUpdateConfirmation,
	hasPlatformValidationErrors,
	hasPlatformSaveErrors,
	isBrandingInProgress,
	isSplitByPlatform,
}: SocialMediaNavigationProps) => {
	const userSession = useUserSession();
	// A draft publishes nothing, so a problem that only stops publishing leaves it savable. The rules
	// the API applies to every save are another matter: per-platform content length is bounded
	// whatever the status, so a draft breaking one is rejected and the button has to say so.
	const blockedByPlatformLimits =
		!!hasPlatformSaveErrors || (!!hasPlatformValidationErrors && source?.status !== Api.PostStatus.Draft);
	const blockedBySave = blockedByPlatformLimits || !!isBrandingInProgress;
	const hasContent = hasPublishedContent(context);

	const accountHasAiGeneratedContentFeatureEnabled = (
		impersonationCtx?.account ? impersonationCtx.account : userSession.account
	).features?.contentGeneration?.enabled;

	// SocialMediaPostReportViewModel (the report-flow variant of `source`) has no `sendWithCompliance` getter.
	const sendWithCompliance = source instanceof Api.SocialMediaPostViewModel ? source.sendWithCompliance : undefined;
	const wasAlreadyComplianceApprovedForSend = wasApprovedForCompliantSocialSend(
		source?.status,
		source?.approvalRequests?.length,
		sendWithCompliance
	);
	const canShowAiContentAssistant = accountHasAiGeneratedContentFeatureEnabled && !wasAlreadyComplianceApprovedForSend;

	// The assistant writes the base body, and once the post is split no platform publishes that
	// body — the wording would land where nothing can see it. Off until the post is one again.
	const aiAssistantButton = (
		<AiButton
			disabled={isSplitByPlatform}
			onClick={onAIContentGenerationAssistantButtonClicked}
			title='Write post with our AI assistant'
			size='xs'
		/>
	);

	return (
		<div
			className={css(
				bs.flex,
				bs.boxBorder,
				bs.justifyBetween,
				bs.px4,
				bs.py2,
				bs.border0,
				bs.borderB,
				bs.borderSolid,
				bs.borderMentionDark,
				bs.itemsCenter
			)}
		>
			{!canShowAiContentAssistant ? null : isSplitByPlatform ? (
				// A disabled button swallows its own hover events, so the tooltip hangs off a wrapper.
				<SimpleTooltip title='Turn off Customize by platform to write this post with our AI assistant'>
					<div className={css(bs.inlineFlex)}>{aiAssistantButton}</div>
				</SimpleTooltip>
			) : (
				aiAssistantButton
			)}

			<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1, bs.justifyEnd)}>
				<ButtonGroup>
					{canSaveTemplate ? (
						<>
							<Button
								size='sm'
								variant='secondary'
								onClick={handleSave}
								disabled={updateTemplateMutation.isPending || blockedBySave}
							>
								Save Template
							</Button>
							<Dropdown>
								<DropdownTrigger
									render={<Button size='sm' variant='secondary' disabled={!hasContent || blockedBySave} />}
								>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownItem onClick={onSaveAsTemplateClicked}>Save as</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</>
					) : (
						<Button size='sm' onClick={onSaveAsTemplateClicked} disabled={!hasContent || blockedBySave}>
							Save as
						</Button>
					)}
				</ButtonGroup>
				{newPost && !impersonationCtx && context?.selectedCreator?.id === userSession?.user?.id ? (
					<Button variant='secondary' size='sm' onClick={onSaveAsButtonClicked} disabled={!hasContent || blockedBySave}>
						Save as draft
					</Button>
				) : null}
				{newPost && !impersonationCtx && context?.selectedCreator?.id !== userSession?.user?.id ? (
					<Tooltip delayDuration={0}>
						<TooltipTrigger>
							<InfoIcon className={css(bs.block)} fillColor={brandPrimary} />
						</TooltipTrigger>
						<TooltipContent>Draft posts can only be saved when selecting yourself as sender.</TooltipContent>
					</Tooltip>
				) : null}
				{source?.status === Api.PostStatus.Draft ? (
					<Button variant='destructive-secondary' size='sm' onClick={onShowCancelConfirmation}>
						<span>Delete</span>
					</Button>
				) : null}
				{source ? (
					<Button size='sm' onClick={onShowUpdateConfirmation} disabled={!context?.hasChanges || blockedBySave}>
						<span>Save</span>
					</Button>
				) : null}
			</div>
		</div>
	);
};

interface StatusSpecificDetailsProps {
	postToEdit: Api.SocialMediaPostViewModel;
	scheduledPost: Api.SocialMediaPostViewModel;
	context: IEditSocialMediaPostContext;
	isComplianceFlow: boolean;
	sendWithComplianceEmail: string;
	setSendWithComplianceEmail: (value: string) => void;
	isSchedulingInProgress: boolean;
	impersonationCtx: Api.IImpersonationContext;
	handleScheduleForLater: () => void;
	handleScheduleWithoutCompliance: () => void;
	handlePostNow: () => void;
	checkComplianceEmail: () => boolean;
	lastUsedComplianceRecipientEmail: string;
	handleChangeDate: () => void;
	onViewCitationsClicked: () => void;
	source: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	usesRegEd?: boolean;
	/** Officer files from the review portal, so the advisor fills no questionnaire here. */
	officerFilesRegEd?: boolean;
	officerReviewerMissing?: boolean;
	regEdProbeLoading?: boolean;
	complianceFormOpen?: boolean;
	onComplianceFormOpenChange?: (next: boolean) => void;
	onScheduleRegEdCompliance?: (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata) => void;
	/** Last-submitted metadata — re-seeds the form when the composer remounts after the send-options round trip. */
	regEdMetadata?: IRegEdSubmissionMetadata | null;
	regEdRevisesApprovalRequestId?: string | null;
	/** Owned by the editor, which is where the post's save lives. */
	onReviseCompliance?: (submission: IRegEdSubmissionView) => void | Promise<void>;
	onReviseDueDateChange?: (dueDate: Date | null) => void;
	/** Present while a revision is reopened but unfiled; `needsFiling` says which step still owes. */
	revisionOwed?: { needsFiling: boolean } | null;
	onRetryRevisionClicked?: () => void;
	onFinishRegEdSubmission?: (submission: IRegEdSubmissionView) => Promise<unknown>;
	isFinishingRegEdSubmission?: boolean;
	isRetryingRevision?: boolean;
	isRevising?: boolean;
	/** True while a platform version breaks a limit the API enforces. */
	hasPlatformValidationErrors?: boolean;
	isBrandingInProgress?: boolean;
	isSplitByPlatform?: boolean;
}

export const StatusSpecificDetails = observer(
	({
		postToEdit,
		scheduledPost,
		context,
		isComplianceFlow,
		sendWithComplianceEmail,
		setSendWithComplianceEmail,
		isSchedulingInProgress,
		impersonationCtx,
		handleScheduleForLater,
		handleScheduleWithoutCompliance,
		handlePostNow,
		checkComplianceEmail,
		lastUsedComplianceRecipientEmail,
		handleChangeDate,
		onViewCitationsClicked,
		source,
		usesRegEd,
		officerFilesRegEd,
		officerReviewerMissing,
		regEdProbeLoading,
		complianceFormOpen,
		onComplianceFormOpenChange,
		onScheduleRegEdCompliance,
		regEdMetadata,
		regEdRevisesApprovalRequestId,
		onReviseCompliance,
		onReviseDueDateChange,
		revisionOwed,
		onRetryRevisionClicked,
		onFinishRegEdSubmission,
		isFinishingRegEdSubmission,
		isRetryingRevision,
		isRevising,
		hasPlatformValidationErrors,
		isBrandingInProgress,
		isSplitByPlatform,
	}: StatusSpecificDetailsProps) => {
		const userSession = useUserSession();
		const statusDetails = getStatusGroupOptions(postToEdit || scheduledPost);
		// A post that breaks a platform's limits is rejected on send, so publishing waits for a fix.
		const blockedByPlatformLimits = !!hasPlatformValidationErrors;
		const hasContent = hasPublishedContent(context);
		const canBypassCompliance = accountAllowsComplianceBypass(
			userSession,
			new ImpersonationContextViewModel(impersonationCtx),
			usesRegEd
		);
		// Post Now bypasses the filing on RegEd but routes through compliance on internal, and is
		// withheld once the prompt asks the question, where it would be a third answer to it.
		const canPostNow = !regEdProbeLoading && (!usesRegEd || (canBypassCompliance && !onScheduleRegEdCompliance));
		const isScheduleDisabled =
			!context.postTargets?.length ||
			!hasContent ||
			isSchedulingInProgress ||
			blockedByPlatformLimits ||
			!!isBrandingInProgress ||
			!!officerReviewerMissing;
		// Internal only: on RegEd the choice lives inside the questionnaire prompt.
		const showScheduleWithoutCompliance = !impersonationCtx && !usesRegEd && isComplianceFlow && canBypassCompliance;
		const showPostNow = !impersonationCtx && canPostNow && userSession?.user?.id === context.selectedCreator?.id;
		// The alternatives share one row above the primary action, so the main path stays the widest
		// and last button in the column.
		const secondaryCtas =
			showScheduleWithoutCompliance || showPostNow ? (
				<div className={css(styleSheet.ctaSecondaryRow)}>
					{showScheduleWithoutCompliance ? (
						<button
							className={css(bs.ctaButtonReverse, styleSheet.ctaSecondaryButton)}
							disabled={isScheduleDisabled}
							onClick={handleScheduleWithoutCompliance}
						>
							<span>Schedule without Compliance</span>
						</button>
					) : null}
					{showPostNow ? (
						<button
							className={css(bs.ctaButtonReverse, styleSheet.ctaSecondaryButton)}
							disabled={isScheduleDisabled}
							onClick={handlePostNow}
						>
							<span>Post Now</span>
						</button>
					) : null}
				</div>
			) : null;

		const history = useHistory();
		// A compliance rejection cancels the post, so the generic cancellation notice would report the
		// same event a second time — louder, and without the reviewer's reason or the way to act on it.
		const cancelledByCompliance =
			usesRegEd &&
			!!source?.approvalRequests?.some(request => request.response?.status === Api.CampaignApprovalStatus.Rejected);
		const handleReviseCompliance = (submission: IRegEdSubmissionView) => {
			if (onReviseCompliance) {
				setPendingRevision(submission);
				// Dropped with the same reasoning as the date below: the dialog does not stay mounted, so a
				// draft the advisor walked away from must not outrank the round's real filing.
				setReviseDraft(null);
				// Both copies re-seed together: the host's is what actually reschedules, so leaving it set
				// from a previous visit would apply a date this dialog never showed.
				const seededDueDate = source?.dueDate ? new Date(source.dueDate) : null;
				setReviseDueDate(seededDueDate);
				onReviseDueDateChange?.(seededDueDate);
				setReviseCarriedFrom(null);
				reviseCarryPending.current = true;
				setPickingReviseDate(false);
				setIsReviseDialogOpen(true);
				return;
			}
			history.push(buildRegEdSocialReviseLocation(source, submission));
		};

		const [isReviseDialogOpen, setIsReviseDialogOpen] = React.useState(false);
		const [pendingRevision, setPendingRevision] = React.useState<IRegEdSubmissionView | null>(null);
		// A rejected post keeps the date it was scheduled against; approving a revision on a past date
		// leaves it Pending and it never posts.
		const [reviseDueDate, setReviseDueDate] = React.useState<Date | null>(null);
		const [pickingReviseDate, setPickingReviseDate] = React.useState(false);
		// Held here because the picker unmounts the prompt, which seeds only once per mount.
		const [reviseDraft, setReviseDraft] = React.useState<IRegEdSubmissionMetadata | null>(null);
		const reviseDueDateIsPast = getRegEdReviseDateState('post', reviseDueDate).blocksApproval;
		// The date a stale posting date was carried forward from, kept so the dialog can say it moved.
		const [reviseCarriedFrom, setReviseCarriedFrom] = React.useState<Date | null>(null);
		const reviseCarryPending = React.useRef(false);
		// Carried to the firm's notice period, not just past today: the revision's review date is refreshed
		// to that period and first use follows the post, so anything sooner files short notice unasked.
		const { data: reviseSchema, isLoading: reviseSchemaLoading } = useRegEdMetadataSchemaQuery(
			reviseDraft?.format || pendingRevision?.metadata?.format || null,
			{ enabled: isReviseDialogOpen, impersonationContext: impersonationCtx }
		);
		React.useEffect(() => {
			if (!isReviseDialogOpen || !reviseCarryPending.current || !reviseDueDate || reviseSchemaLoading) {
				return;
			}
			reviseCarryPending.current = false;
			const carried = carrySendForward(reviseDueDate, findReviewMinimumDate(reviseSchema));
			if (carried.getTime() !== reviseDueDate.getTime()) {
				setReviseCarriedFrom(reviseDueDate);
				setReviseDueDate(carried);
				onReviseDueDateChange?.(carried);
			}
		}, [isReviseDialogOpen, reviseDueDate, reviseSchema, reviseSchemaLoading, onReviseDueDateChange]);

		// Compliance is still forced on an officer-files account; the questionnaire is what moves, to the
		// officer's portal. So the advisor keeps the compliance-pending schedule button and loses the panel.
		const showRegEdPrompt = usesRegEd && !officerFilesRegEd && !!onScheduleRegEdCompliance;

		switch (statusDetails) {
			case SocialMediaPostStatusDetails.CancelledOrFailed:
				return (
					<>
						<div className={css(styleSheet.card)}>
							<div className={css(styleSheet.cardTitle)}>Send Time</div>
							<div className={css(styleSheet.cardActions)}>
								{scheduledPost ? (
									<time dateTime={scheduledPost.dueDate}>
										{moment(scheduledPost.dueDate).format('dddd, MMMM Do, h:mm A')}
									</time>
								) : (
									<time dateTime={source?.dueDate}>{moment(source?.dueDate).format('dddd, MMMM Do, h:mm A')}</time>
								)}
							</div>
						</div>
						{source?.status === Api.PostStatus.Cancelled && !cancelledByCompliance ? (
							<div className={css(styleSheet.card)}>
								<div className={css(styleSheet.cardActions)}>
									<span className={css(styleSheet.cardSubSection)}>
										<DangerIcon />
										{context?.postTargets?.find(x => x.state?.status === Api.PostTargetStatus.Posted)
											? `Part of this post was cancelled on ${moment(source?.lastModifiedDate).format('MMMM Do, h:mm A')}`
											: `This post was cancelled on ${moment(source?.lastModifiedDate).format('MMM Do, h:mm A')}`}
									</span>
								</div>
							</div>
						) : null}
						{/* A RegEd rejection cancels the post, which lands it in this branch — without the panel here
						    the reviewer's decision, their notes and Revise & Resubmit would all be unreachable, and
						    the piece would read as an ordinary cancellation. Self-gating: it renders nothing when the
						    post was never filed. */}
						{revisionOwed ? (
							<RegEdRevisionOwedCard
								isRetrying={isRetryingRevision}
								needsFiling={revisionOwed.needsFiling}
								onRetry={onRetryRevisionClicked}
							/>
						) : null}
						{usesRegEd && source?.id ? (
							<RegEdComplianceStatus
								impersonationContext={impersonationCtx}
								isRevising={isRevising}
								reviseDisabled={!!revisionOwed}
								onRevise={handleReviseCompliance}
								onFinishSubmission={onFinishRegEdSubmission}
								isFinishing={isFinishingRegEdSubmission}
								ownerUserId={context.selectedCreator?.id}
								parentId={source.id}
								styles={[styleSheet.regEdComplianceCard]}
							/>
						) : null}
						<Dialog
							open={isReviseDialogOpen}
							onOpenChange={open => {
								if (!isRevising) {
									setIsReviseDialogOpen(open);
								}
							}}
						>
							<DialogPopup showCloseButton={!isRevising}>
								<DialogHeader>
									<DialogTitle>
										{isRegEdResendableFailure(pendingRevision)
											? 'Edit and send for compliance review'
											: 'Revise and resubmit for compliance review'}
									</DialogTitle>
									<DialogDescription>
										{isRegEdResendableFailure(pendingRevision)
											? 'Your edits are saved and the post is filed with your compliance system again. Check the compliance details before you send.'
											: 'Your edits are saved and the reviewer sees the post again. Update the compliance details if the reviewer asked you to.'}
									</DialogDescription>
								</DialogHeader>
								{pickingReviseDate ? (
									<CampaignDateTimePicker
										ctaText='Select'
										// Not an email: the non-sending-day and send-interval rules are email's, and applying
										// them here would refuse the off-hours date the piece was already scheduled for.
										ignoreDisabledDays
										ignoreEmailTimeIntervals
										initialValue={reviseDueDate ?? new Date()}
										onCancel={() => setPickingReviseDate(false)}
										onSave={date => {
											setReviseDueDate(date);
											onReviseDueDateChange?.(date);
											setReviseCarriedFrom(null);
											setPickingReviseDate(false);
										}}
									/>
								) : (
									<>
										{reviseDueDate ? (
											<>
												<ScheduleDate
													disabled={isRevising}
													onClick={() => setPickingReviseDate(true)}
													scheduleDate={reviseDueDate.toISOString()}
													showChangeButton={true}
													title='Posting Date'
												/>
												<RegEdReviseDateNotice carriedFrom={reviseCarriedFrom} date={reviseDueDate} kind='post' />
											</>
										) : null}
										{/* Editable, not replayed: a reviewer can reject because of the form, and the requested
								    review date inside the answers goes stale between rounds. */}
										<RegEdComplianceApprovalPrompt
											campaignType={CampaignType.Social}
											impersonationContext={impersonationCtx}
											initialAnswers={reviseDraft?.answers ?? pendingRevision?.metadata?.answers}
											initialFormat={reviseDraft?.format ?? pendingRevision?.metadata?.format}
											onFormStateChange={setReviseDraft}
											isRevision
											revisesApprovalRequestId={getRegEdRevisesApprovalRequestId(pendingRevision)}
											ownerUserId={context.selectedCreator?.id}
											revisionSendDate={reviseDueDate}
											socialProviders={context.postTargets?.map(target => target.provider)}
											disabled={reviseDueDateIsPast}
											isLoading={isRevising}
											onScheduleRegEdCompliance={async (_, metadata) => {
												if (!pendingRevision) {
													return;
												}
												try {
													await onReviseCompliance?.({ ...pendingRevision, metadata });
												} finally {
													setIsReviseDialogOpen(false);
												}
											}}
											styles={[styleSheet.reviseDialogForm]}
											submitLabel={isRegEdResendableFailure(pendingRevision) ? 'Send for Review' : 'Revise & Resubmit'}
										/>
									</>
								)}
							</DialogPopup>
						</Dialog>
					</>
				);
			case SocialMediaPostStatusDetails.NewOrDraft:
				return (
					<>
						{source ? (
							<>
								<div className={css(styleSheet.card)}>
									<div className={css(styleSheet.cardTitle)}>Last Updated</div>
									<div className={css(styleSheet.cardActions)}>
										<time dateTime={source?.lastModifiedDate}>
											{moment(source?.lastModifiedDate).format('dddd, MMMM Do, h:mm A')}
										</time>
									</div>
								</div>
								{source?.actor && !source.actor.isLevitateSupport ? (
									<div className={css(styleSheet.card)}>
										<div className={css(styleSheet.cardTitle)}>Created By</div>
										<div className={css(styleSheet.cardActions)}>
											<span className={css(styleSheet.approvalStatusTitle)}>{source?.actor?.name}</span>
										</div>
									</div>
								) : null}
							</>
						) : null}
						<div
							className={css(styleSheet.ctaContainer, showRegEdPrompt && styleSheet.ctaContainerRegEd)}
							data-reged-host={showRegEdPrompt ? '' : undefined}
						>
							<div
								className={css(styleSheet.ctaContainerButtons, showRegEdPrompt && styleSheet.ctaContainerButtonsRegEd)}
							>
								{isBrandingInProgress ? (
									<FieldError>
										Your branding is still being applied. The post can be sent once every image is back — the version
										being branded shows how far along it is.
									</FieldError>
								) : null}
								{showRegEdPrompt ? (
									<RegEdComplianceApprovalPrompt
										impersonationContext={impersonationCtx}
										campaignType={CampaignType.Social}
										socialProviders={context.postTargets?.map(target => target.provider)}
										collapsible
										disabled={
											!context.postTargets?.length || !hasContent || blockedByPlatformLimits || !!isBrandingInProgress
										}
										isLoading={isSchedulingInProgress}
										initialFormat={regEdMetadata?.format}
										initialAnswers={regEdMetadata?.answers}
										revisesApprovalRequestId={regEdRevisesApprovalRequestId}
										ownerUserId={context.selectedCreator?.id}
										onScheduleRegEdCompliance={onScheduleRegEdCompliance}
										onScheduleWithoutCompliance={
											canBypassCompliance && !impersonationCtx ? handleScheduleWithoutCompliance : undefined
										}
										expanded={complianceFormOpen}
										onExpandedChange={onComplianceFormOpenChange}
										styles={[styleSheet.regEdPromptInSidebar]}
									/>
								) : (
									<>
										{isComplianceFlow && !usesRegEd && (
											<Input
												id='user-emailAddress'
												autoComplete='off'
												placeholder='Please enter Compliance Email'
												type='text'
												onBlur={() => {
													if (!checkComplianceEmail()) {
														return;
													}
													localStorage.setItem(lastUsedComplianceRecipientEmail, sendWithComplianceEmail);
												}}
												onChange={ev => {
													setSendWithComplianceEmail(ev.target.value);
												}}
												styles={[styleSheet.complianceInput]}
												value={sendWithComplianceEmail}
											/>
										)}
										{officerReviewerMissing ? (
											<FieldError>
												No compliance email is set for this account, so nobody would be asked to review this post. Ask
												your administrator to add one before scheduling.
											</FieldError>
										) : null}
										{secondaryCtas}
										<button
											className={css(bs.ctaButton, styleSheet.ctaPrimaryButton)}
											disabled={isScheduleDisabled}
											onClick={handleScheduleForLater}
										>
											<span>{isComplianceFlow ? 'Schedule Post with Compliance Pending' : 'Schedule for later'}</span>
										</button>
									</>
								)}
							</div>
							{blockedByPlatformLimits ? (
								<div className={css(styleSheet.platformLimitNotice)}>
									<DangerIcon
										backgroundColor={THEME.colors.danger.main}
										borderColor={THEME.colors.danger.light}
										className={css(styleSheet.platformLimitNoticeIcon)}
									/>
									<span>
										{isSplitByPlatform
											? 'Fix the platform versions marked in red before scheduling.'
											: 'This post is over a platform limit. Fix the errors above the post to schedule it.'}
									</span>
								</div>
							) : null}
						</div>
					</>
				);
			case SocialMediaPostStatusDetails.NotCancelledFailedDraftOrUnknown:
				return (
					<>
						<CampaignDetailsSummary
							campaign={scheduledPost}
							onChangeScheduleClicked={handleChangeDate}
							disabled={context?.hasChanges}
							onReviseCompliance={onReviseCompliance}
							// The same revision can be started from this branch, so it needs the same in-flight
							// guard and retry affordance the cancelled branch gets — without them a failed filing
							// leaves a reopened round with no way to finish it.
							isRevising={isRevising}
							isRetryingRevision={isRetryingRevision}
							onRetryRevisionClicked={onRetryRevisionClicked}
							revisionOwed={revisionOwed}
							onFinishSubmission={onFinishRegEdSubmission}
							isFinishingSubmission={isFinishingRegEdSubmission}
							onViewCitationsClicked={onViewCitationsClicked}
						/>
						{source?.status === Api.PostStatus.PartiallySucceeded || source?.status === Api.PostStatus.Started ? (
							<div className={css(styleSheet.card)}>
								<div className={css(styleSheet.cardActions)}>
									<span className={css(styleSheet.cardSubSection)}>
										<DangerIcon />
										{`Some targets for this post ${
											source?.status === Api.PostStatus.PartiallySucceeded ? 'were rejected.' : 'are still in progress.'
										}`}
									</span>
								</div>
							</div>
						) : null}
					</>
				);
			default:
				return;
		}
	}
);

interface MediaOverlayOptionsProps {
	context: IEditSocialMediaPostContext;
	/**
	 * Called after the overlay logo toggle; used for auto-save in the admin content calendar, where
	 * the Save button is hidden. Its presence means the caller owns marking the post changed.
	 */
	onOverlayLogoChange?: () => void;
}

/**
 * The logo overlay changes what the media looks like, so it sits with the preview rather than under
 * the scheduling buttons, where it read as a footnote to an action it has nothing to do with.
 */
const MediaOverlayOptions = observer(({ context, onOverlayLogoChange }: MediaOverlayOptionsProps) => {
	const [expanded, setExpanded] = React.useState(context.overlayLogo);

	const handleToggle = (overlayLogo: boolean) => {
		context.setOverlayLogo(overlayLogo);

		if (!onOverlayLogoChange) {
			context.setHasChanges(true);
			return;
		}

		setTimeout(() => onOverlayLogoChange(), 0);
	};

	return (
		<div className={css(styleSheet.advancedOptions)}>
			<AdvancedOptionsButton isCollapsed={!expanded} onClick={() => setExpanded(!expanded)} />
			{/* Not the animating Collapsible: it springs to a height measured once, so the logo size line
			    — which only appears once the toggle is on — gets clipped by a stale target. */}
			{expanded ? (
				<div>
					<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
						<Toggle
							className={css(styleSheet.toggle)}
							id='logo-toggle'
							isOn={context.overlayLogo}
							text={!context.overlayLogo ? 'Overlay Logo Off' : 'Overlay Logo On'}
							onToggleCheckChanged={handleToggle}
							uncheckedColor='#B1B4B6'
						/>
						<TinyPopover
							align='end'
							anchor={<InfoIcon fillColor={charmGray} />}
							autoCloseOtherPopoversOnHover
							dismissOnOutsideAction
							anchorStyles={[styleSheet.infoPopoverToggle]}
							placement={['top', 'right']}
							toggleOnHover
							type={PopoverType.lightBlue}
						>
							<div className={css(styleSheet.popover)}>
								This will overlay your account&apos;s logo on the post&apos;s media at the moment the post gets posted.
								Click on the image preview to see the overlay.
							</div>
						</TinyPopover>
					</div>
					{context.overlayLogo ? (
						<span className={css(styleSheet.logoSizeLabel)}>
							Logo Size: {context.logoSize ?? Api.DEFAULT_LOGO_SIZE}px
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
});

interface DefaultComposerContextProps {
	context: IEditSocialMediaPostContext;
	expirationDateInEffect: boolean;
	onRenderStatusSpecificDetails: (coordination: {
		complianceFormOpen: boolean;
		onComplianceFormOpenChange: (next: boolean) => void;
	}) => JSX.Element;
	overlayLogo: boolean;
	impersonationCtx: Api.IImpersonationContext;
	hideWhiteBackground: boolean;
	logoSize: number;
	platformCustomization: IPlatformCustomizationView;
	/** False when the account, the post's status, or its media leave nothing for the toggle to do. */
	showMediaOverlayOptions: boolean;
	onOverlayLogoChange?: () => void;
}

export const DefaultComposerContext = ({
	context,
	expirationDateInEffect,
	onRenderStatusSpecificDetails,
	overlayLogo,
	impersonationCtx,
	hideWhiteBackground,
	logoSize,
	platformCustomization: platform,
	showMediaOverlayOptions,
	onOverlayLogoChange,
}: DefaultComposerContextProps) => {
	// The preview and the questionnaire share one column and each fill it, so only one opens at a
	// time — otherwise whichever loses is scrolled out of reach.
	const [openPanel, setOpenPanel] = React.useState<'preview' | 'compliance' | null>('preview');
	const previewOpen = openPanel === 'preview';
	// Held stable across renders: the preview returns to its first platform whenever its targets change,
	// and a fresh array on every keystroke would count as a change.
	const previewTargets = React.useMemo(() => dedupeByProvider(context.postTargets), [context.postTargets]);
	return (
		<>
			<CollapsibleV2
				className={css(styleSheet.previewCollapsible)}
				onOpenChange={next => setOpenPanel(next ? 'preview' : null)}
				open={previewOpen}
			>
				<CollapsibleTrigger className={css(styleSheet.previewCollapsibleTrigger)}>
					<span>{previewOpen ? 'Hide preview' : 'Show preview'}</span>
					<ChevronDown
						className={css(
							styleSheet.previewCollapsibleChevron,
							previewOpen && styleSheet.previewCollapsibleChevronOpen
						)}
						height={16}
						width={16}
					/>
				</CollapsibleTrigger>
				<CollapsiblePanel styles={[styleSheet.previewCollapsiblePanel]}>
					<PreviewSocialMediaPost
						postTargets={previewTargets}
						overlayLogo={overlayLogo}
						postContent={
							platform.isSplit
								? applyPlatformMentions(platform.activeContent, platform.activePlatform, context.facebookMentions)
								: applyFacebookMentions(context.postContent, context.facebookMentions)
						}
						postImages={platform.isSplit ? platform.activeAttachments : context.postImages}
						// Each platform's own version, so the hidden layers that hold the card's height measure
						// what that platform publishes rather than the version on screen.
						getPlatformVersion={
							platform.isSplit
								? provider => {
										const version = findPlatformCustomization(context.platformCustomizations, provider);
										return {
											postContent: applyPlatformMentions(version?.content, provider, context.facebookMentions),
											postImages: version?.attachments ?? [],
										};
									}
								: undefined
						}
						selectedProvider={platform.isSplit ? platform.activePlatform : undefined}
						onSelectedProviderChange={platform.isSplit ? platform.setActivePlatform : undefined}
						impersonationCtx={impersonationCtx}
						hideWhiteBackground={hideWhiteBackground}
						postCreatorUserId={context.selectedCreator?.id}
						logoSize={logoSize}
						onLogoSizeChange={context.setLogoSize}
						templateId={context.template?.id}
					>
						<>
							{expirationDateInEffect ? (
								<div className={css(styleSheet.expirationDateContainer)}>
									<ExpiryIcon fill={warningDark} />
									<span>
										{`This post is available to send until ${moment(context.template.schedule.expirationDate).format(
											'MM/DD/YY'
										)}`}
									</span>
								</div>
							) : null}
						</>
					</PreviewSocialMediaPost>
				</CollapsiblePanel>
			</CollapsibleV2>

			{showMediaOverlayOptions ? (
				<MediaOverlayOptions context={context} onOverlayLogoChange={onOverlayLogoChange} />
			) : null}

			{onRenderStatusSpecificDetails({
				complianceFormOpen: openPanel === 'compliance',
				// Closing the form hands the column back; hiding the preview deliberately does not force
				// the form open in return.
				onComplianceFormOpenChange: next => setOpenPanel(next ? 'compliance' : 'preview'),
			})}
		</>
	);
};
